import { DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AbstractControl, FormControl, ValidatorFn, Validators } from "@angular/forms";
import { startWith } from "rxjs/operators";
import { TableCols } from "../interfaces/static-data";

const numberedOptions: () => {id: string, displayValue: string}[]  = () => {
  return new Array(30).fill(1).map((value, index) => ({id: `${index+1}`, displayValue: `${index+1}`}))
}

export const RecurringFeeOptions = {
  evaluationFrequency: [
    {id: '1M', displayValue: 'Monthly'},
    {id: '3M', displayValue: 'Quarterly'},
    {id: '6M', displayValue: 'Semiannually'},
    {id: '12M', displayValue: 'Annually'},
  ],
  rollConvention: [
    {id: 'PRECEDING', displayValue: 'Preceding'},
    {id: 'FOLLOWING', displayValue: 'Following'},
  ],
  evaluationPeriodType: [
    {id: 'CALENDAR', displayValue: 'Calendar'},
    {id: 'FREE_CHOICE', displayValue: 'Free Choice'},
  ],
  startOfFirstPeriod: [
    {id: '1', displayValue: 'January'},
    {id: '2', displayValue: 'February'},
    {id: '3', displayValue: 'March'},
    {id: '4', displayValue: 'April'},
    {id: '5', displayValue: 'May'},
    {id: '6', displayValue: 'June'},
    {id: '7', displayValue: 'July'},
    {id: '8', displayValue: 'August'},
    {id: '9', displayValue: 'September'},
    {id: '10', displayValue: 'October'},
    {id: '11', displayValue: 'November'},
    {id: '12', displayValue: 'December'},
  ],
  evaluationDay: [
    {id: 'Last day of the period', displayValue: 'Last day of the period'},
    ...numberedOptions()
  ]
}

export const LAST_DAY_OF_PERIOD = 'Last day of the period';

const approvalCutoffValidation: ValidatorFn = (control: AbstractControl) => {
  const evaluationDayValue = control.parent?.get('evaluationDay')?.value;

  if (!control || ! control.value || !evaluationDayValue || evaluationDayValue === LAST_DAY_OF_PERIOD) {
    return null;
  }

  return Number(control.value) >= Number(evaluationDayValue) ? null : { approvalCutoffMinValue: 'Value must be equal or greater than Evaluation Day value' }
}

const approvalLagValidation: ValidatorFn = (control: AbstractControl) => {
  const approvalCutoffValue = control.parent?.get('approvalCutoff')?.value;

  if (!control || ! control.value || !approvalCutoffValue) {
    return null;
  }

  return Number(control.value) >= Number(approvalCutoffValue) ? null : { approvalLagMinValue: 'Value must be equal or greater than Approval Cutoff value' }
}

const startFirstPeriodAction = (control: FormControl, controlName: string, destroyRef: DestroyRef) => {
  control.parent?.get('evaluationPeriodType')?.valueChanges
    .pipe(
      startWith('CALENDAR'),
      takeUntilDestroyed(destroyRef)
    )
    .subscribe({
      next: (evaluationPeriodTypeValue: 'CALENDAR' | 'FREE_CHOICE') => {
        if (evaluationPeriodTypeValue === 'CALENDAR') {
          control.setValue(null);
          control.clearValidators();
          control.disable();
        } else {
          control.setValue(null);
          control.addValidators(Validators.required);
          control.enable();
        }
      }
    })
}

export const RECURRING_FEE: TableCols[] = [
  {
    key: 'name',
    displayValue: 'Name',
    validators: [Validators.required],
  },
  {
    key: 'evaluationFrequency',
    displayValue: 'Frequency',
    type: 'select',
    items: RecurringFeeOptions.evaluationFrequency,
    validators: [Validators.required],
    defaultValue: '1M'
  },
  {
    key: 'rollConvention',
    displayValue: 'Roll Convention',
    type: 'select',
    items: RecurringFeeOptions.rollConvention,
    validators: [Validators.required]
  },
  {
    key: 'evaluationPeriodType',
    displayValue: 'Evaluation Period Type',
    type: 'select',
    items: RecurringFeeOptions.evaluationPeriodType,
    validators: [Validators.required],
    defaultValue: 'CALENDAR'
  },
  {
    key: 'startOfFirstPeriod',
    displayValue: 'Start of First Period',
    type: 'select',
    items: RecurringFeeOptions.startOfFirstPeriod,
    action: startFirstPeriodAction
  },
  {
    key: 'evaluationDay',
    displayValue: 'Evaluation Day',
    type: 'select',
    items: RecurringFeeOptions.evaluationDay,
    validators: [Validators.required],
    selectOptions: {
      searchable: true
    },
    hint: 'The day of the period on which fees should be calculated when this template is assigned to a supplement agreement. If ‘Last day of the period’ is selected, the last day of the period will be used.'
  },
  {
    key: 'approvalCutoff',
    displayValue: 'Product Approval Fees Cutoff',
    type: 'number',
    validators: [Validators.required, approvalCutoffValidation],
    defaultValue: 0,
    hint: 'Number of days to be added to the product approval period\'s start/end dates when identifying fees to include in the approval workflow. This value must be greater than or equal to Evaluation Day (except when Evaluation Day is ‘Last day of the period’).'
  },
  {
    key: 'approvalLag',
    displayValue: 'Approval Start Lag',
    type: 'number',
    validators: [Validators.required, approvalLagValidation],
    hint: 'Number of days to add to the product approval period\'s end date before launching the approval workflow for that period. This value must be greater than Product Approval Fee Days Offset.'
  },
]
